// Enhanced Investor KYC + Sequential Workflow Governance UI primitives.
//
// All components are presentational + read-only EXCEPT EnhancedKycForm, which
// submits through investorService.submitEnhancedKyc (geo-api brokered). Every
// component tolerates null / partial data and renders safe empty states.
//
// Reused by: InvestorDashboard (portal) and any admin oversight surface.

import React, { useState } from 'react';
import {
  ShieldCheck, CheckCircle2, Circle, Lock, Clock, XCircle,
  FileSignature, CreditCard, FileText, ArrowRight,
} from 'lucide-react';
import {
  FormWizard,
  type WizardStepDef,
  type WizardReviewSection,
  type WizardSubmitState,
} from '@/components/portal/forms/FormWizard';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/eds';
import {
  investorService,
  computeKycCompleteness,
  kycStatusMeta,
  deriveWorkflowStages,
  nextWorkflowAction,
  WORKFLOW_ORDER,
  WORKFLOW_LABELS,
  type InvestorKycProfile,
  type InvestorProfileSummary,
  type WorkflowStage,
  type WorkflowStageKey,
  type WorkflowStageStatus,
  type DocumentLifecycleEntry,
  type EnhancedKycPayload,
} from '@/services/investorService';

/* ───────────────── KYC status badge ───────────────── */

export const KycStatusBadge: React.FC<{
  profile: InvestorKycProfile | null;
}> = ({ profile }) => {
  const meta = kycStatusMeta(profile?.kycStatus);

  const tone:
    | 'success'
    | 'danger'
    | 'info'
    | 'warning'
    | 'neutral' =
    meta.tone === 'approved'
      ? 'success'
      : meta.tone === 'rejected'
        ? 'danger'
        : meta.tone === 'review'
          ? 'info'
          : meta.tone === 'pending'
            ? 'warning'
            : 'neutral';

  return (
    <Badge
      tone={tone}
      icon={
        <ShieldCheck
          className="h-3.5 w-3.5"
          aria-hidden="true"
        />
      }
      role="status"
      aria-label={`KYC status: ${meta.label}`}
    >
      KYC: {meta.label}
    </Badge>
  );
};

/* ───────────────── Onboarding completeness meter ───────────────── */

export const KycCompletenessMeter: React.FC<{
  profile: InvestorKycProfile | null;
  compact?: boolean;
}> = ({ profile, compact }) => {
  const score = computeKycCompleteness(profile);

  const progressTone =
    score >= 100
      ? 'bg-eds-success'
      : score >= 60
        ? 'bg-eds-warning'
        : 'bg-eds-muted-fg';

  const content = (
    <>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-eds-body text-xs font-medium uppercase tracking-wider text-eds-muted-fg">
          Onboarding completeness
        </span>

        <span className="font-mono text-sm font-semibold text-eds-fg">
          {score}%
        </span>
      </div>

      <div
        className="h-2 overflow-hidden rounded-eds-pill bg-eds-surface-2"
        role="progressbar"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`h-full rounded-eds-pill transition-all ${progressTone}`}
          style={{ width: `${score}%` }}
        />
      </div>

      {!compact && (
        <p className="mt-2 font-eds-body text-xs text-eds-muted-fg">
          {score >= 100
            ? 'All required onboarding fields captured.'
            : 'Complete your KYC profile to unlock the full investor workflow.'}
        </p>
      )}
    </>
  );

  if (compact) {
    return content;
  }

  return (
    <Card className="p-4" elevation="none">
      {content}
    </Card>
  );
};

/* ───────────────── Sequential workflow tracker ───────────────── */

const STAGE_ICON: Record<WorkflowStageKey, React.ComponentType<{ className?: string }>> = {
  loi: FileText,
  nda: FileSignature,
  payment: CreditCard,
  mou: FileSignature,
};

const stageStatusMeta = (s: WorkflowStageStatus) => {
  switch (s) {
    case 'approved':
      return {
        label: 'Approved',
        Icon: CheckCircle2,
        badgeTone: 'success' as const,
        iconClass: 'text-eds-success',
        surfaceClass: 'border-eds-success/30 bg-eds-success/8',
      };

    case 'rejected':
      return {
        label: 'Rejected',
        Icon: XCircle,
        badgeTone: 'danger' as const,
        iconClass: 'text-eds-danger',
        surfaceClass: 'border-eds-danger/30 bg-eds-danger/8',
      };

    case 'under_review':
    case 'submitted':
      return {
        label: s === 'under_review' ? 'Under review' : 'Submitted',
        Icon: Clock,
        badgeTone: 'info' as const,
        iconClass: 'text-eds-info',
        surfaceClass: 'border-eds-info/30 bg-eds-info/8',
      };

    case 'in_progress':
    case 'available':
      return {
        label: s === 'in_progress' ? 'In progress' : 'Available',
        Icon: s === 'available' ? Circle : Clock,
        badgeTone: 'warning' as const,
        iconClass: 'text-eds-warning',
        surfaceClass: 'border-eds-warning/40 bg-eds-warning/10',
      };

    default:
      return {
        label: 'Locked',
        Icon: Lock,
        badgeTone: 'neutral' as const,
        iconClass: 'text-eds-muted-fg',
        surfaceClass: 'border-eds-border bg-eds-surface-2',
      };
  }
};

export const WorkflowProgressTracker: React.FC<{
  serverStages?: WorkflowStage[] | null;
  profile: InvestorProfileSummary | null;
  onJump?: (tab: string) => void;
}> = ({ serverStages, profile, onJump }) => {
  const stages = deriveWorkflowStages(serverStages, profile);

  const tabFor: Record<WorkflowStageKey, string> = {
    loi: 'agreements',
    nda: 'agreements',
    payment: 'payments',
    mou: 'agreements',
  };

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-eds-display text-sm font-semibold text-eds-fg">
          <FileSignature
            className="h-4 w-4 text-eds-emerald-2"
            aria-hidden="true"
          />
          Investor workflow — LOI → NDA → Payment → MOU
        </h3>

        <Badge tone="neutral">
          Sequential
        </Badge>
      </div>

      <ol className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        {stages.map((stage, idx) => {
          const meta = stageStatusMeta(stage.status);
          const Icon = STAGE_ICON[stage.key];
          const locked = stage.status === 'locked';

          const actionable =
            stage.status === 'available' ||
            stage.status === 'in_progress' ||
            stage.status === 'rejected';

          return (
            <li
              key={stage.key}
              className={`relative rounded-eds-md border p-3 ${meta.surfaceClass}`}
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="font-eds-body text-[10px] font-bold text-eds-muted-fg">
                  {idx + 1}
                </span>

                <Icon
                  className={`h-4 w-4 ${meta.iconClass}`}
                  aria-hidden="true"
                />

                <meta.Icon
                  className={`ml-auto h-3.5 w-3.5 ${meta.iconClass}`}
                  aria-hidden="true"
                />
              </div>

              <div className="font-eds-body text-xs font-semibold text-eds-fg">
                {WORKFLOW_LABELS[stage.key]}
              </div>

              <div className="mt-1">
                <Badge tone={meta.badgeTone}>
                  {meta.label}
                </Badge>
              </div>

              {stage.note && (
                <div className="mt-1 truncate font-eds-body text-[10px] text-eds-muted-fg">
                  {stage.note}
                </div>
              )}

              {actionable && onJump && (
                <button
                  type="button"
                  onClick={() => onJump(tabFor[stage.key])}
                  className="mt-2 inline-flex items-center gap-1 font-eds-body text-[11px] font-medium text-eds-emerald-2 underline underline-offset-2 hover:text-eds-forest"
                >
                  Continue
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </button>
              )}

              {locked && (
                <div className="mt-2 flex items-center gap-1 font-eds-body text-[10px] text-eds-muted-fg">
                  <Lock className="h-3 w-3" aria-hidden="true" />
                  Complete previous stage
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
};

/* ───────────────── Required next action banner ───────────────── */

export const RequiredNextAction: React.FC<{
  serverStages?: WorkflowStage[] | null;
  profile: InvestorProfileSummary | null;
  kyc: InvestorKycProfile | null;
  onJump?: (tab: string) => void;
}> = ({ serverStages, profile, kyc, onJump }) => {
  const completeness = computeKycCompleteness(kyc);

  if (completeness < 100) {
    return (
      <Alert
        tone="warning"
        title="Next action: complete your KYC profile"
        icon={
          <ShieldCheck
            className="h-5 w-5"
            aria-hidden="true"
          />
        }
        className="items-center"
      >
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span>
            Your onboarding is {completeness}% complete.
          </span>

          {onJump && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onJump('kyc')}
            >
              Open KYC
            </Button>
          )}
        </div>
      </Alert>
    );
  }

  const stages = deriveWorkflowStages(serverStages, profile);
  const next = nextWorkflowAction(stages);

  if (!next) {
    return (
      <Alert
        tone="success"
        title="Workflow up to date"
        icon={
          <CheckCircle2
            className="h-5 w-5"
            aria-hidden="true"
          />
        }
      >
        All workflow stages are up to date. No action required.
      </Alert>
    );
  }

  const tabFor: Record<WorkflowStageKey, string> = {
    loi: 'agreements',
    nda: 'agreements',
    payment: 'payments',
    mou: 'agreements',
  };

  return (
    <Alert
      tone="info"
      title={`Next action: ${next.label}`}
      icon={
        <ArrowRight
          className="h-5 w-5"
          aria-hidden="true"
        />
      }
      className="items-center"
    >
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <span>
          Continue the GIISDEC investor workflow.
        </span>

        {onJump && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onJump(tabFor[next.key])}
          >
            Continue
          </Button>
        )}
      </div>
    </Alert>
  );
};

/* ───────────────── Document lifecycle panel ───────────────── */

const documentUploadTone = (
  status: DocumentLifecycleEntry['uploadStatus'],
): 'success' | 'info' | 'danger' | 'neutral' => {
  if (status === 'verified') return 'success';
  if (status === 'uploaded') return 'info';
  if (status === 'rejected') return 'danger';
  return 'neutral';
};

export const DocumentLifecyclePanel: React.FC<{
  rows: DocumentLifecycleEntry[];
}> = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        title="No workflow documents yet."
        message="LOI, NDA, payment receipts and MOU documents will appear here as each stage progresses."
        icon={<FileText className="h-5 w-5" />}
        className="py-8"
      />
    );
  }

  return (
    <Card className="overflow-hidden" elevation="sm">
      <Table>
        <THead>
          <TR>
            <TH>Stage</TH>
            <TH>Document</TH>
            <TH>Upload</TH>
            <TH>Signature</TH>
          </TR>
        </THead>

        <TBody>
          {rows.map((r) => (
            <TR key={r.id}>
              <TD>
                <span className="font-eds-body text-xs font-medium uppercase text-eds-fg">
                  {r.stage}
                </span>
              </TD>

              <TD>{r.title}</TD>

              <TD>
                <Badge tone={documentUploadTone(r.uploadStatus)}>
                  {r.uploadStatus}
                </Badge>
              </TD>

              <TD>
                <span className="font-eds-body text-eds-muted-fg">
                  {r.signatureStatus ?? '—'}
                </span>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
};

/* ───────────────── Enhanced KYC form ───────────────── */

const inputCls = 'w-full rounded-eds-md border border-eds-border bg-eds-surface-2 px-3 py-2 font-eds-body text-sm text-eds-fg focus:outline-none focus:ring-2 focus:ring-eds-ring';

const KField: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <label className="block">
    <span className="font-eds-body text-xs font-medium uppercase tracking-wider text-eds-fg">
      {label}{required && <span className="text-eds-danger"> *</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

// KYC / Onboarding — converted to the unified multi-step wizard (WP2.2-R3).
// Fields, `name` attributes, defaults and the submit payload are UNCHANGED; the
// wizard only reorganises data entry into steps. All inputs remain mounted so
// `new FormData(form)` yields exactly the same EnhancedKycPayload as before.
const KYC_ID_TYPE_LABEL: Record<string, string> = {
  passport: 'Passport', national_id: 'National ID', drivers_license: "Driver's licence", company_cert: 'Company certificate',
};
const KYC_CLASS_LABEL: Record<string, string> = {
  strategic: 'Strategic partner', institutional: 'Institutional', private_equity: 'Private equity',
  individual: 'Individual / private', government: 'Government / sovereign',
};

export const EnhancedKycForm: React.FC<{ profile: InvestorKycProfile | null }> = ({ profile }) => {
  const [submitState, setSubmitState] = useState<WizardSubmitState>({ phase: 'idle' });
  const d = profile;

  // Payload construction is byte-for-byte identical to the pre-wizard form.
  const onSubmit = async (form: HTMLFormElement) => {
    setSubmitState({ phase: 'submitting' });
    const fd = new FormData(form);
    const split = (v: FormDataEntryValue | null) =>
      String(v || '').split(',').map((s) => s.trim()).filter(Boolean);
    const payload: EnhancedKycPayload = {
      email: String(fd.get('email') || ''),
      phone: String(fd.get('phone') || ''),
      organizationName: String(fd.get('organizationName') || ''),
      organizationRegistration: String(fd.get('organizationRegistration') || '') || undefined,
      organizationAddress: String(fd.get('organizationAddress') || '') || undefined,
      countryOfOperation: String(fd.get('countryOfOperation') || ''),
      businessCategory: String(fd.get('businessCategory') || '') || undefined,
      representativeName: String(fd.get('representativeName') || ''),
      representativeRole: String(fd.get('representativeRole') || '') || undefined,
      representativeEmail: String(fd.get('representativeEmail') || '') || undefined,
      idType: String(fd.get('idType') || 'passport'),
      idNumber: String(fd.get('idNumber') || ''),
      taxId: String(fd.get('taxId') || '') || undefined,
      investorClassification: String(fd.get('investorClassification') || '') || undefined,
      projectInterests: split(fd.get('projectInterests')),
      mineralInterests: split(fd.get('mineralInterests')),
      declarationAccepted: fd.get('declaration') === 'on',
    };
    const res = await investorService.submitEnhancedKyc(payload).catch(() => ({ error: 'submit_failed' } as { error: string; status?: number }));
    if (res && 'error' in res && res.error) {
      // 403 is a governed Restricted state (never a raw error), per RBAC policy.
      if ((res as { status?: number }).status === 403) {
        setSubmitState({ phase: 'restricted', message: 'Your account is not currently permitted to submit KYC. Please contact the GIISDEC desk.' });
      } else {
        setSubmitState({ phase: 'error', message: 'Submission could not be completed. Please review the fields and try again.' });
      }
    } else {
      setSubmitState({ phase: 'success', message: 'KYC submitted via geo-api. Your profile is now under review.' });
    }
  };

  const steps: WizardStepDef[] = [
    {
      key: 'contact', label: 'Contact', description: 'How the GIISDEC desk reaches you.',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KField label="Investor email" required><input name="email" type="email" defaultValue={d?.email} className={inputCls} required /></KField>
          <KField label="Official phone" required><input name="phone" defaultValue={d?.phone} className={inputCls} required /></KField>
        </div>
      ),
    },
    {
      key: 'organization', label: 'Organization',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KField label="Organization / company" required><input name="organizationName" defaultValue={d?.organizationName} className={inputCls} required /></KField>
          <KField label="Registration number"><input name="organizationRegistration" defaultValue={d?.organizationRegistration} className={inputCls} /></KField>
          <div className="md:col-span-2">
            <KField label="Registered address"><textarea name="organizationAddress" rows={2} defaultValue={d?.organizationAddress} className={inputCls} /></KField>
          </div>
          <KField label="Country of operation" required><input name="countryOfOperation" defaultValue={d?.countryOfOperation} className={inputCls} required /></KField>
          <KField label="Business category / type">
            <input name="businessCategory" defaultValue={d?.businessCategory} className={inputCls} placeholder="Mining, smelting, logistics…" />
          </KField>
        </div>
      ),
    },
    {
      key: 'representative', label: 'Representative',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KField label="Full name" required><input name="representativeName" defaultValue={d?.representativeName} className={inputCls} required /></KField>
          <KField label="Role / title"><input name="representativeRole" defaultValue={d?.representativeRole} className={inputCls} /></KField>
          <KField label="Representative email"><input name="representativeEmail" type="email" defaultValue={d?.representativeEmail} className={inputCls} /></KField>
        </div>
      ),
    },
    {
      key: 'identification', label: 'Identification',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KField label="ID type" required>
            <select name="idType" defaultValue={d?.idType || 'passport'} className={inputCls}>
              <option value="passport">Passport</option>
              <option value="national_id">National ID</option>
              <option value="drivers_license">Driver's licence</option>
              <option value="company_cert">Company certificate</option>
            </select>
          </KField>
          <KField label="ID number" required><input name="idNumber" defaultValue={d?.idNumber} className={inputCls} required /></KField>
          <KField label="Tax identification (TIN)"><input name="taxId" defaultValue={d?.taxId} className={inputCls} /></KField>
        </div>
      ),
    },
    {
      key: 'classification', label: 'Classification',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KField label="Investor classification">
            <select name="investorClassification" defaultValue={d?.investorClassification || ''} className={inputCls}>
              <option value="">Select…</option>
              <option value="strategic">Strategic partner</option>
              <option value="institutional">Institutional</option>
              <option value="private_equity">Private equity</option>
              <option value="individual">Individual / private</option>
              <option value="government">Government / sovereign</option>
            </select>
          </KField>
          <KField label="Project interest areas">
            <input name="projectInterests" defaultValue={d?.projectInterests?.join(', ')} className={inputCls} placeholder="Comma separated" />
          </KField>
          <KField label="Concession / mineral interests">
            <input name="mineralInterests" defaultValue={d?.mineralInterests?.join(', ')} className={inputCls} placeholder="Iron ore, manganese…" />
          </KField>
        </div>
      ),
    },
  ];

  const buildReview = (v: Record<string, string>): WizardReviewSection[] => [
    { stepKey: 'contact', title: 'Contact', rows: [
      { label: 'Investor email', value: v.email }, { label: 'Official phone', value: v.phone },
    ] },
    { stepKey: 'organization', title: 'Organization', rows: [
      { label: 'Organization / company', value: v.organizationName },
      { label: 'Registration number', value: v.organizationRegistration },
      { label: 'Registered address', value: v.organizationAddress },
      { label: 'Country of operation', value: v.countryOfOperation },
      { label: 'Business category', value: v.businessCategory },
    ] },
    { stepKey: 'representative', title: 'Authorized representative', rows: [
      { label: 'Full name', value: v.representativeName },
      { label: 'Role / title', value: v.representativeRole },
      { label: 'Representative email', value: v.representativeEmail },
    ] },
    { stepKey: 'identification', title: 'Identification & tax', rows: [
      { label: 'ID type', value: KYC_ID_TYPE_LABEL[v.idType || 'passport'] || v.idType },
      { label: 'ID number', value: v.idNumber },
      { label: 'Tax identification (TIN)', value: v.taxId },
    ] },
    { stepKey: 'classification', title: 'Classification & interests', rows: [
      { label: 'Investor classification', value: KYC_CLASS_LABEL[v.investorClassification || ''] || '' },
      { label: 'Project interest areas', value: v.projectInterests },
      { label: 'Concession / mineral interests', value: v.mineralInterests },
    ] },
  ];

  const declaration = (
    <div className="space-y-3">
      <p className="text-xs text-eds-muted-fg">
        Supporting document uploads (certificate of incorporation, ID, tax registration) are handled securely via geo-api in the Documents tab once your profile is submitted.
      </p>
      <div className="flex items-center gap-2">
        <input id="declaration" name="declaration" type="checkbox" required />
        <label htmlFor="declaration" className="text-sm text-eds-fg">
          I declare that the information provided is accurate and complete.
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <KycStatusBadge profile={profile} />
        <div className="flex-1 min-w-[200px]"><KycCompletenessMeter profile={profile} compact /></div>
      </div>
      <FormWizard
        title="KYC / Onboarding"
        description="Complete your verification. Progress is saved in this session as you move between steps."
        steps={steps}
        buildReview={buildReview}
        declaration={declaration}
        submitLabel="Submit KYC"
        submitState={submitState}
        onSubmit={onSubmit}
        onReset={() => setSubmitState({ phase: 'idle' })}
      />
    </div>
  );
};
